import {makeAutoObservable, runInAction} from 'mobx';
import {networkOptimization} from '../../../../lib/api';
import {FetchGeoJsonResponse} from '../types';

/**
 * Manages patient roster data and patient GeoJSON for map visualization.
 * Handles loading patient rosters and geographic distribution of patients.
 */
export class PatientStore {
    geoJson: FetchGeoJsonResponse | null = null;
    private _patientRosters: any[] = [];
    private _loadingPatientRosters = false;

    constructor() {
        makeAutoObservable(this);
    }

    get patientRosters(): any[] {
        return this._patientRosters;
    }

    set patientRosters(value: any[]) {
        this._patientRosters = value;
    }

    get loadingPatientRosters(): boolean {
        return this._loadingPatientRosters;
    }

    async loadPatientRosters(controller: AbortController): Promise<void> {
        // Prevent duplicate API calls - early return if already loading
        if (this._loadingPatientRosters) {
            return;
        }

        runInAction(() => {
            this._loadingPatientRosters = true;
        });

        try {
            const response = await networkOptimization.listPatientRosters(controller.signal);
            runInAction(() => {
                this._patientRosters = response.rosters || [];
                this._loadingPatientRosters = false;
            });
        } catch (error) {
            runInAction(() => {
                this._loadingPatientRosters = false;
            });
            throw error;
        }
    }

    async loadPatientGeoJson(
        filters: Record<string, string>,
        runId: number,
        revisionId: number,
        distanceCalcType: string,
        controller: AbortController,
        usePopulation = false
    ): Promise<void> {
        const serviceCall = usePopulation
            ? networkOptimization.fetchPatientPopulationGeoJson
            : networkOptimization.fetchPatientGeoJson;

        const response = await serviceCall(
            filters,
            runId,
            revisionId,
            distanceCalcType,
            controller.signal
        );
        runInAction(() => {
            this.geoJson = response;
        });
    }

    async loadDisruptionAnalysisPatientGeoJson(
        filters: Record<string, string>,
        runId: number,
        revisionId: number,
        controller: AbortController,
        usePopulation = false
    ): Promise<void> {
        const serviceCall = usePopulation
            ? networkOptimization.fetchDAPatientPopulationGeoJson
            : networkOptimization.fetchDAPatientGeoJson;

        const response = await serviceCall(
            filters,
            runId,
            revisionId,
            controller.signal
        );
        runInAction(() => {
            this.geoJson = response;
        });
    }
}
